import React from 'react';
import audioVideoImage from '../images/services/audio-video-ai.webp';
import polarisImage from '../images/services/polaris-healthcare.webp';
import intellimeshImage from '../images/services/intellimesh-medical-imaging.webp';
import relvianImage from '../images/services/setu-skills.webp';
import { products } from '../data/products';

const voiceVerse = products.find(product => product.id === 'voiceverse');
const polaris = products.find(product => product.id === 'polaris');

const services = [
    {
        id: 'audio-video-ai',
        title: 'Voice & Media AI',
        subtitle: 'VoiceVerse APIs',
        image: audioVideoImage,
        alt: 'Illustration of a video editing workstation with a microphone, audio waveform, and translation symbol',
        description: 'Build voice experiences and creative media workflows into your applications with VoiceVerse APIs. Combine speech, image and video creation, story planning, translation, and AI-assisted review, with API access arranged through MCN.',
        href: voiceVerse.href,
        cta: voiceVerse.cta,
    },
    {
        id: 'polaris-healthcare',
        title: 'Polaris AI',
        subtitle: 'Healthcare Voice & IVR',
        image: polarisImage,
        alt: 'Illustration of a clinic reception desk with an appointment calendar, headset, and conversation panel',
        description: 'Connect voice and IVR assistants with OpenEMR for appointment scheduling, patient enquiries, intake, and staff handoffs. Prioritize patient privacy with on-premises or cloud deployment options aligned with your clinic’s data access and control requirements.',
        href: polaris.href,
        cta: polaris.cta,
    },
    {
        id: 'intellimesh-medical-imaging',
        title: 'IntelliMesh',
        subtitle: 'Medical Imaging AI',
        image: intellimeshImage,
        alt: 'Conceptual illustration of a clinician reviewing brain scan images and model overlays at a workstation',
        description: 'Integrate medical imaging foundation models into research and clinician review workflows. Bring image understanding, contextual information, and model outputs together in applications tailored to healthcare teams.',
    },
    {
        id: 'relvian-business',
        title: 'Relvian',
        subtitle: 'AI & Voice for Small Business',
        image: relvianImage,
        alt: 'Conceptual illustration of a microphone beside a laptop displaying a profile and business information',
        description: 'Help small teams prepare for customer conversations, gather requirements, and find answers in approved business information. Turn conversations into clear next steps and reviewed follow-up tasks.',
    },
];

const Services = () => {
    return (
        <div id="services" className="scroll-mt-24 overflow-hidden bg-gray-100 py-12">
            <section aria-labelledby="ai-solutions-title" className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12">
                <div className="mx-auto mb-10 max-w-5xl text-center">
                    <h2 id="ai-solutions-title" className="my-2 text-3xl text-blue-900 font-bold">Audio, Video &amp; Multimodal AI Solutions</h2>
                    <div className="mx-auto my-4 w-24 border-b-4 border-blue-900" aria-hidden="true" />
                    <p className="text-lg lg:text-xl font-semibold text-blue-900">
                        Apply our core capabilities to media production, healthcare operations, medical imaging, and small-business conversations. Each workflow combines the technologies its users need.
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {services.map((service) => (
                        <article key={service.id} aria-labelledby={`${service.id}-title`} className="flex flex-col overflow-hidden rounded-xl bg-white border border-blue-100 shadow-md">
                            <img src={service.image} alt={service.alt} width="800" height="800" loading="lazy" decoding="async" className="block aspect-square w-full object-cover" />
                            <div className="flex flex-1 flex-col p-5 lg:p-6">
                                <h3 id={`${service.id}-title`} className="text-xl font-bold text-blue-900">
                                    {service.title}
                                    {service.subtitle && <span className="mt-1 block text-base font-semibold">{service.subtitle}</span>}
                                </h3>
                                <p className="mt-4 text-base leading-relaxed text-gray-600">{service.description}</p>
                                {service.href && <a href={service.href} className="mt-auto pt-6 font-semibold text-blue-900 underline underline-offset-4 hover:text-blue-700">{service.cta} <span aria-hidden="true">→</span></a>}
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section aria-label="How we work" className="mx-auto mt-12 grid max-w-6xl gap-8 px-6 sm:grid-cols-2">
                <div><h3 className="text-2xl font-bold text-blue-900">Build around your users</h3><p className="mt-4 text-lg leading-relaxed text-gray-600">Combine AI models, media processing, and application tools around a specific task. Keep domain knowledge and user review close to the decisions that matter.</p></div>
                <div><h3 className="text-2xl font-bold text-blue-900">Connect with your systems</h3><p className="mt-4 text-lg leading-relaxed text-gray-600">Integrate with existing records, learning materials, and business workflows. Choose the model and deployment approach that fit the application.</p></div>
            </section>
        </div>
    );
};
export default Services;
